/**
 * reportRenderer.ts
 *
 * Pure-function utilities for executing AdminReportSectionDto scripts and
 * producing HTML output strings.  These functions have no React dependency and
 * can be used from any component (ReportView, WellVisitView inline display,
 * PDF export, etc.).
 */

import { AdminResponseDto } from '../types/AdminResponseDto';
import { AdminReportSectionDto, AdminAssessmentReportDto, AdminPreLoadDto } from '../types/adminAssessment';
import { AppProfilePHIDto } from '../types/api/appProfile';
import { renderChart as renderChartSvg, type ChartConfig } from './chartRenderer';

// ── Section helpers ──────────────────────────────────────────────────────────

/**
 * Collect all AdminReportSectionDto objects from every subReport within a
 * report, in order.
 */
export const getAllReportSections = (
  report: AdminAssessmentReportDto
): AdminReportSectionDto[] => {
  const sections: AdminReportSectionDto[] = [];
  report.subReports?.forEach(subReport => {
    subReport.reportSections?.forEach(section => sections.push(section));
  });
  return sections;
};

/**
 * Collect sections retaining the parent subReport name for display purposes.
 */
export const getAllReportSectionsWithSubReport = (
  report: AdminAssessmentReportDto
): { subReportName: string; section: AdminReportSectionDto }[] => {
  const result: { subReportName: string; section: AdminReportSectionDto }[] = [];
  report.subReports?.forEach(subReport => {
    subReport.reportSections?.forEach(section =>
      result.push({ subReportName: subReport.dName, section })
    );
  });
  return result;
};

// ── Hub-report config ───────────────────────────────────────────────────────

/** One child report a hub report links to, plus the label for its nav button. */
export interface ReportHubChild {
  reportId: string;
  label: string;
  /** Optional instruction text shown to the right of the nav button (3rd `:`-delimited field
   * of a hubChildReports pair — see getReportHubConfig). Empty string when not provided. */
  instruction: string;
}

/**
 * Parsed hub-report configuration, sourced entirely from a report's own
 * reportPreloads (see getReportHubConfig). A "hub" report renders nav buttons to
 * other reports in the same assessment and gates its own continue button until
 * they've been opened — the React "TestDrive"/demo report pattern where a single
 * report acts as a container with "View Client Report" / "View Clinician Report"
 * buttons and a Next button that finishes the flow.
 */
export interface ReportHubConfig {
  /** True only when a `hubReport` preload is truthy AND at least one `hubChildReports` pair parsed. */
  enabled: boolean;
  children: ReportHubChild[];
  /** When true, the hub's continue button stays disabled until every child has been opened. */
  requireAllViewed: boolean;
  /** Label for the hub screen's continue button. */
  continueLabel: string;
  /** Optional centered message shown on the hub screen just above the continue button
   * (`hubFooter` preload). Empty string when not provided. */
  footerText: string;
}

const isTruthyPreload = (value: string | undefined): boolean => {
  const v = (value ?? '').trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'y';
};

/**
 * Read a report's hub configuration from its reportPreloads. Recognized names
 * (case-insensitive):
 *
 *   hubReport           truthy ("1"/"true"/"yes") → this report is a hub
 *   hubChildReports     comma-separated `reportId:Label` or `reportId:Label:Instruction`
 *                       triples, e.g.
 *                       "5:View Clinician Report:This report comes to the clinician,
 *                        6:View Client Report:Goes to your client (optional)"
 *                       The instruction (everything after the 2nd colon) shows to the right
 *                       of that child's nav button; omit it and only the button renders.
 *   hubRequireAllViewed "0"/"false" to allow continue without opening the children (default: require)
 *   hubContinueLabel    label for the hub continue button (default: "Next")
 *   hubFooter           centered message shown on the hub screen just above the continue button
 *
 * Returns { enabled:false, ... } for any report without a truthy hubReport
 * preload (or with no parseable child pairs), so callers can compute this
 * unconditionally and the non-hub render path is unaffected.
 */
export const getReportHubConfig = (
  report?: AdminAssessmentReportDto
): ReportHubConfig => {
  const disabled: ReportHubConfig = {
    enabled: false,
    children: [],
    requireAllViewed: true,
    continueLabel: 'Next',
    footerText: '',
  };
  if (!report?.reportPreloads?.length) return disabled;

  const preloads = new Map<string, string>();
  report.reportPreloads.forEach(p => {
    if (p?.name) preloads.set(p.name.trim().toLowerCase(), p.value ?? '');
  });

  if (!isTruthyPreload(preloads.get('hubreport'))) return disabled;

  const children: ReportHubChild[] = [];
  (preloads.get('hubchildreports') ?? '')
    .split(',')
    .map(pair => pair.trim())
    .filter(Boolean)
    .forEach(pair => {
      // reportId : label : instruction — reportId is the first `:`-delimited field, label the
      // second, and everything after (which may itself contain colons) the instruction.
      const parts = pair.split(':');
      if (parts.length < 2) return;
      const reportId = parts[0].trim();
      const label = parts[1].trim();
      const instruction = parts.slice(2).join(':').trim();
      if (reportId && label) children.push({ reportId, label, instruction });
    });

  if (children.length === 0) return disabled;

  const requireRaw = (preloads.get('hubrequireallviewed') ?? '').trim().toLowerCase();
  const requireAllViewed = !(requireRaw === '0' || requireRaw === 'false' || requireRaw === 'no');

  const continueLabel = (preloads.get('hubcontinuelabel') ?? '').trim() || 'Next';
  const footerText = (preloads.get('hubfooter') ?? '').trim();

  return { enabled: true, children, requireAllViewed, continueLabel, footerText };
};

// ── Trend chart preloads (enableBarGraph / enableLineGraph) ────────────────────

/** One requested bar graph — a single datavar trended across history. */
export interface TrendBarGraphRequest {
  name: string;
  dataVar: string;
}

/** One series within a requested line graph — a datavar trended across history, with its legend label. */
export interface TrendLineGraphSeriesRequest {
  dataVar: string;
  label: string;
}

/** One requested line graph — one or more series sharing a common date axis. */
export interface TrendLineGraphRequest {
  name: string;
  series: TrendLineGraphSeriesRequest[];
}

export interface TrendChartRequests {
  barGraphs: TrendBarGraphRequest[];
  lineGraphs: TrendLineGraphRequest[];
}

const EMPTY_TREND_CHART_REQUESTS: TrendChartRequests = { barGraphs: [], lineGraphs: [] };

/**
 * Parses a report's `enableBarGraph`/`enableLineGraph` reportPreloads into a structured
 * description of every requested trend chart — see buildTrendChartOverrides, which turns this
 * into the actual precomputed labels/values/series data a report script reads via
 * getChartData(). Never throws; malformed input just yields fewer/no parsed entries.
 *
 * Grammar (case-insensitive preload *names* only — graph names/datavars are verbatim/case-sensitive,
 * since they must match responseData/putData names elsewhere):
 *
 *   enableBarGraph   bgname1:datavar||bgname2:datavar||...
 *   enableLineGraph  lgname1:datavar@label,datavar@label,...||lgname2:datavar@label,...
 *                    (the `@label` suffix is optional per datavar — omitted, the datavar name
 *                    itself is used as the series label)
 *
 * Example: parseTrendChartPreloads for reportPreloads containing
 *   enableBarGraph  = "phqTrend:phqTotal||gadTrend:gadTotal"
 *   enableLineGraph = "moodTrend:phqTotal@PHQ-9,gadTotal@GAD-7,sleepScore"
 * returns:
 *   {
 *     barGraphs: [{name:'phqTrend',dataVar:'phqTotal'}, {name:'gadTrend',dataVar:'gadTotal'}],
 *     lineGraphs: [{name:'moodTrend', series:[
 *       {dataVar:'phqTotal',label:'PHQ-9'}, {dataVar:'gadTotal',label:'GAD-7'},
 *       {dataVar:'sleepScore',label:'sleepScore'}, // no @label -> defaults to the datavar name
 *     ]}],
 *   }
 */
export const parseTrendChartPreloads = (reportPreloads?: AdminPreLoadDto[]): TrendChartRequests => {
  if (!reportPreloads?.length) return EMPTY_TREND_CHART_REQUESTS;

  const preloads = new Map<string, string>();
  reportPreloads.forEach(p => {
    if (p?.name) preloads.set(p.name.trim().toLowerCase(), p.value ?? '');
  });

  const barGraphs: TrendBarGraphRequest[] = (preloads.get('enablebargraph') ?? '')
    .split('||')
    .map(chunk => chunk.trim())
    .filter(Boolean)
    .map(chunk => {
      const idx = chunk.indexOf(':');
      if (idx < 0) return null;
      const name = chunk.slice(0, idx).trim();
      const dataVar = chunk.slice(idx + 1).trim();
      if (!name || !dataVar) return null;
      return { name, dataVar };
    })
    .filter((req): req is TrendBarGraphRequest => req !== null);

  const lineGraphs: TrendLineGraphRequest[] = (preloads.get('enablelinegraph') ?? '')
    .split('||')
    .map(chunk => chunk.trim())
    .filter(Boolean)
    .map(chunk => {
      const idx = chunk.indexOf(':');
      if (idx < 0) return null;
      const name = chunk.slice(0, idx).trim();
      const seriesRaw = chunk.slice(idx + 1).trim();
      if (!name || !seriesRaw) return null;

      const series: TrendLineGraphSeriesRequest[] = seriesRaw
        .split(',')
        .map(piece => piece.trim())
        .filter(Boolean)
        .map(piece => {
          const atIdx = piece.indexOf('@');
          const dataVar = (atIdx < 0 ? piece : piece.slice(0, atIdx)).trim();
          const rawLabel = atIdx < 0 ? '' : piece.slice(atIdx + 1).trim();
          if (!dataVar) return null;
          return { dataVar, label: rawLabel || dataVar };
        })
        .filter((s): s is TrendLineGraphSeriesRequest => s !== null);

      if (series.length === 0) return null;
      return { name, series };
    })
    .filter((req): req is TrendLineGraphRequest => req !== null);

  return { barGraphs, lineGraphs };
};

/**
 * 'mm/dd/yy' label for one historical AdminResponseDto — recUTM wins (finer-grained timestamp),
 * falling back to recDate when recUTM is blank/unparseable, and to the raw string when neither
 * parses (never throws).
 */
const formatShortDate = (entry: AdminResponseDto): string => {
  const raw = entry.recUTM || entry.recDate || '';
  let d = new Date(entry.recUTM || '');
  if (isNaN(d.getTime())) {
    d = new Date(entry.recDate || '');
  }
  if (isNaN(d.getTime())) return raw;
  // UTC getters — recUTM/recDate are UTC timestamps, and the admin date shown to a report
  // author shouldn't shift by a day depending on the viewer's local timezone (a midnight-UTC
  // timestamp would otherwise render as the previous day west of Greenwich).
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

/**
 * Turns parsed trend chart requests (see parseTrendChartPreloads) into the precomputed
 * labels/values/series data a report script reads via getChartData() — one shared date axis
 * (formatShortDate of every entry in trendHistory) across every requested bar/line graph, so
 * multi-series line graphs stay aligned even when a given datavar wasn't answered on every date.
 *
 * `trendHistory` must already be resolved/filtered/sorted (same assessmentId, oldest→newest,
 * capped at the administration being viewed) — see ClientReportAssessments.ts's
 * resolveTrendHistoryFromEmbedded/resolveTrendHistoryFromLocal. This function does no date or
 * assessment filtering of its own.
 *
 * A datavar with no answer on some historical entry is left as '' for that point — never
 * substituted with '0' here, since chartRenderer.ts's renderChart() already coerces a
 * non-numeric/blank value to 0 when it actually draws; duplicating that here would just obscure
 * "no answer" from "answered zero" for any future consumer of this Map.
 *
 * Returns a plain Map<string,string> (JSON-encoded values) — merge its entries into the same
 * reportDataOverrides Map passed to generateFullReportHtml before calling it, so getChartData()
 * can see them once the report's scripts run.
 */
export const buildTrendChartOverrides = (
  requests: TrendChartRequests,
  trendHistory: AdminResponseDto[]
): Map<string, string> => {
  const overrides = new Map<string, string>();
  if (requests.barGraphs.length === 0 && requests.lineGraphs.length === 0) {
    return overrides;
  }

  const labels = trendHistory.map(formatShortDate);
  const labelsJson = JSON.stringify(labels);
  const dataVarValues = (dataVar: string): string[] =>
    trendHistory.map(entry => entry.responseData.find(r => r.name === dataVar)?.value ?? '');

  requests.barGraphs.forEach(({ name, dataVar }) => {
    overrides.set(`${name}_labels`, labelsJson);
    overrides.set(`${name}_values`, JSON.stringify(dataVarValues(dataVar)));
  });

  requests.lineGraphs.forEach(({ name, series }) => {
    overrides.set(`${name}_labels`, labelsJson);
    const seriesOut = series.map(({ dataVar, label }) => ({ label, values: dataVarValues(dataVar) }));
    overrides.set(`${name}_series`, JSON.stringify(seriesOut));
  });

  return overrides;
};

// ── Core renderer ────────────────────────────────────────────────────────────

/**
 * Execute a single AdminReportSectionDto's reportScript and return the HTML
 * output string.
 *
 * The script runs in a sandboxed context that exposes:
 *   getData(mvarName)        — answer value from responseData
 *   getPHI(field)            — PHI value (dname, fname, lname, dob, gender, email, or extra[])
 *   getPreload(name)         — reportPreload value, scoped to the whole report (moved up from
 *                              a per-section scope — see reportPreloads param below)
 *   getAdmin(varName)        — AdminResponseDto record/admin field (recId, recUTM, recLang,
 *                              recAssessment, recPractice, id, etc. — NOT an answer/responseData
 *                              value; see adminResponseProperties below). recAssessment/
 *                              recPractice/id are the raw record-level identifiers as stored
 *                              today (assessment content package id, practice id, patcode) —
 *                              not yet resolved to their display names/profile linkage; that
 *                              join is planned for later once profile connections are wired up.
 *   putData(name, value)     — sets a report-scoped variable (e.g. overrideRWidth), readable
 *                              via getData() by this or a later section in the same report,
 *                              and by the caller via the optional reportDataOverrides map
 *                              (see generateFullReportHtml) — mirrors AdminView's putData.
 *   renderChart(config)      — returns an HTML string (a <div> wrapping an inline <svg>, plus
 *                              a legend for multi-series line charts) for a bar or line chart —
 *                              see chartRenderer.ts's ChartConfig. This is plain static markup,
 *                              not a DOM/JS-driven chart — nothing needs to execute once the
 *                              eventual WebView loads it, so append the returned string to obuf
 *                              exactly like any other HTML the script builds. Bar charts are
 *                              single-series only (a series.length > 1 call returns a visible
 *                              inline error); line charts support multiple series.
 *   getChartData(name)       — parsed (JSON.parse'd) precomputed trend chart data, keyed by
 *                              the names an enableBarGraph/enableLineGraph reportPreload
 *                              generates (e.g. getChartData('bgname1_labels'),
 *                              getChartData('bgname1_values')) — see parseTrendChartPreloads/
 *                              buildTrendChartOverrides above. Returns the actual array/object,
 *                              ready to pass straight into renderChart(); null if unset or
 *                              unparseable, never throws.
 *   adminResponse            — full AdminResponseDto
 *   formatDate(dateStr)      — locale-aware date formatter
 *   calculateScore(varNames) — sum of numeric answer values
 *   log(msg)                 — console.log wrapper
 *   Math, parseInt, parseFloat, String, Number, Boolean
 *
 * The script appends to the local variable `obuf` and the function returns it.
 * On execution error the function returns an inline error paragraph (never throws).
 *
 * @param reportPreloads Preloads scoped to the whole report this section belongs to
 *   (AdminAssessmentReportDto.reportPreloads) — every section of the same report sees the
 *   same list, since preloads are no longer a per-section concept.
 * @param reportDataOverrides Optional Map shared across every section of one
 *   generateFullReportHtml() call — putData() writes land here too, so the caller can read
 *   report-scoped variables (e.g. overrideRWidth) back out once generation completes.
 */
export const generateReportSectionHtml = (
  reportSection: AdminReportSectionDto,
  reportPreloads: AdminPreLoadDto[] | undefined,
  responseData: AdminResponseDto,
  phi?: AppProfilePHIDto,
  reportDataOverrides?: Map<string, string>
): string => {
  try {
    // Build data lookup maps
    const dataContext = new Map<string, string>();
    responseData.responseData.forEach(item => {
      dataContext.set(item.name, item.value);
    });

    const preloadContext = new Map<string, string>();
    reportPreloads?.forEach(preload => {
      preloadContext.set(preload.name, preload.value);
    });

    const getPreload = (name: string): string => preloadContext.get(name) ?? '';

    // putData()'s store. Falls back to a call-local Map when the caller (e.g. a standalone
    // generateReportSectionHtml call) didn't supply one, so put/get still work within this one
    // section — but generateFullReportHtml always passes one shared Map across every section of
    // a report, which is what makes a value set in an earlier section visible in a later one.
    const overridesMap = reportDataOverrides ?? new Map<string, string>();

    const getPHI = (field: string): string => {
      if (!phi) return '';
      const key = field.toLowerCase();
      const direct: Record<string, string> = {
        dname:  phi.dname,
        fname:  phi.fname,
        lname:  phi.lname,
        email:  phi.email,
        dob:    phi.dob,
        gender: phi.gender,
      };
      if (key in direct) return direct[key];
      return phi.extra.find(e => e.name.toLowerCase() === key)?.value ?? '';
    };

    // AdminResponseDto's own record/admin fields — what getAdmin() reads, as opposed to
    // getData()'s answer/responseData lookup. Both a camelCase and the C# PascalCase name are
    // registered for each field so a script can call getAdmin('recUTM') or getAdmin('RecUTM')
    // interchangeably, matching this codebase's established both-casings convention.
    const adminResponseProperties = new Map<string, string>();
    const setAdminProp = (camel: string, pascal: string, value: string | undefined) => {
      const v = value ?? '';
      adminResponseProperties.set(camel, v);
      adminResponseProperties.set(pascal, v);
    };
    setAdminProp('recId',        'RecId',        responseData.recId);
    setAdminProp('recDate',      'RecDate',      responseData.recDate);
    setAdminProp('recUTM',       'RecUTM',       responseData.recUTM);
    setAdminProp('id',           'ID',           responseData.id);
    setAdminProp('recPatient',   'RecPatient',   responseData.recPatient);
    setAdminProp('recAssessment','RecAssessment',responseData.recAssessment);
    setAdminProp('surveybList',  'SurveybList',  responseData.surveybList);
    setAdminProp('recStatus',    'RecStatus',    responseData.recStatus);
    setAdminProp('recClient',    'RecClient',    responseData.recClient);
    setAdminProp('recPractice',  'RecPractice',  responseData.recPractice);
    setAdminProp('recSite',      'RecSite',      responseData.recSite);
    setAdminProp('recStation',   'RecStation',   responseData.recStation);
    setAdminProp('recSN',        'RecSN',        responseData.recSN);
    setAdminProp('recYear',      'RecYear',      responseData.recYear);
    setAdminProp('recQtr',       'RecQtr',       responseData.recQtr);
    setAdminProp('recMon',       'RecMon',       responseData.recMon);
    setAdminProp('recWeek',      'RecWeek',      responseData.recWeek);
    setAdminProp('recDay',       'RecDay',       responseData.recDay);
    setAdminProp('duration',     'Duration',     responseData.duration);
    setAdminProp('recProvider',  'RecProvider',  responseData.recProvider);
    setAdminProp('recLang',      'RecLang',      responseData.recLang);

    const scriptContext = {
      data:         dataContext,
      responseData: responseData.responseData,
      adminResponse: responseData,
      phiData:      phi ?? null,
      getPHI,
      preloadData:  preloadContext,
      getPreload,
      getAdmin: (varName: string) => adminResponseProperties.get(varName) || '',
      // Answer data (dataContext, from responseData) wins on a name collision; otherwise falls
      // back to whatever putData has stored — e.g. a data string (a warning icon's base64, say)
      // stashed once in an early section and reused via getData() in every later one, without
      // re-embedding it in the script/HTML each time.
      getData:  (varName: string) => {
        const entry = responseData.responseData.find(r => r.name === varName);
        if (entry?.value) return entry.value;
        return overridesMap.get(varName) || '';
      },
      // Sets a report-scoped variable, readable via getData() by this section and (through the
      // one Map generateFullReportHtml shares across every section) any later section in the
      // same report — without mutating the caller's actual answer data. The caller (ReportView)
      // reads overridesMap back too, e.g. for overrideRWidth (page width) and its Debug
      // Information "Report Data (putData)" display.
      putData: (name: string, value: string) => {
        overridesMap.set(name, String(value));
      },
      // Precomputed trend chart data (see parseTrendChartPreloads/buildTrendChartOverrides
      // above) — reads the same overridesMap putData()/getData() share, JSON.parse'd back into
      // the actual array/object a script can hand straight to renderChart(). Never throws;
      // unset/unparseable names return null.
      getChartData: (name: string): unknown => {
        try {
          return JSON.parse(overridesMap.get(name) ?? 'null');
        } catch {
          return null;
        }
      },
      // Static SVG chart markup — see chartRenderer.ts. Never throws (bad config -> a visible
      // inline error string), so a script can always safely obuf += renderChart(...).
      renderChart: (chartConfig: ChartConfig) => renderChartSvg(chartConfig),
      formatDate: (dateStr: string) => {
        try {
          return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
          });
        } catch {
          return dateStr;
        }
      },
      calculateScore: (varNames: string[]) =>
        varNames.reduce((sum, varName) => {
          const value = dataContext.get(varName);
          const numValue = parseInt(value || '0', 10);
          return sum + (isNaN(numValue) ? 0 : numValue);
        }, 0),
      log: (message: string) => console.log('📊 ReportScript:', message),
      Math,
      parseInt,
      parseFloat,
      String,
      Number,
      Boolean,
    };

    // eslint-disable-next-line no-new-func
    const scriptFunction = new Function('context', `
      const {
        data,
        responseData,
        adminResponse,
        phiData,
        getPHI,
        preloadData,
        getPreload,
        getAdmin,
        getData,
        putData,
        getChartData,
        renderChart,
        getResponseValue,
        formatDate,
        calculateScore,
        log,
        Math,
        parseInt,
        parseFloat,
        String,
        Number,
        Boolean
      } = context;

      let obuf = '';

      ${reportSection.reportScript}

      return obuf;
    `);

    const result = scriptFunction(scriptContext);
    return result || '';
  } catch (error) {
    console.error('❌ Error generating report section:', error);
    return `<p style="color:#e74c3c;font-family:sans-serif">Error in report section "${
      reportSection.dName
    }": ${error instanceof Error ? error.message : String(error)}</p>`;
  }
};

/**
 * Run all sections in a report in order, concatenate their HTML output, and
 * return the full report HTML string.
 *
 * @param report          AdminAssessmentReportDto to render
 * @param responseData    AdminResponseDto containing answer data
 * @param phi             Optional PHI context for getPHI() calls in scripts
 * @param editedScripts   Optional map of reportSectionId → overridden script
 *                        (used when an editor has unsaved changes)
 * @param reportDataOverrides Optional Map that every section's putData() call writes into —
 *                        pass an empty Map in and read it back afterward (e.g.
 *                        reportDataOverrides.get('overrideRWidth')) to control page-level
 *                        rendering from report scripts, mirroring AdminView's overrideQWidth.
 */
export const generateFullReportHtml = (
  report: AdminAssessmentReportDto,
  responseData: AdminResponseDto,
  phi?: AppProfilePHIDto,
  editedScripts?: Record<string, string>,
  reportDataOverrides?: Map<string, string>
): string => {
  const sections = getAllReportSections(report);
  const parts: string[] = [];
  // Always pass ONE shared Map into every section — even when the caller didn't supply one —
  // so a value an earlier section's putData() stashes (e.g. a base64 icon reused across the
  // whole report) is visible to getData() in every later section, not just re-readable by the
  // caller afterward.
  const overrides = reportDataOverrides ?? new Map<string, string>();
  for (const section of sections) {
    const effectiveSection =
      editedScripts?.[section.reportSectionId] !== undefined
        ? { ...section, reportScript: editedScripts[section.reportSectionId] }
        : section;
    const html = generateReportSectionHtml(effectiveSection, report.reportPreloads, responseData, phi, overrides);
    if (html) parts.push(html);
  }
  return parts.join('\n') || '<p>No output returned from report scripts.</p>';
};

// ── WebView wrapper ──────────────────────────────────────────────────────────

/**
 * Wrap a raw HTML output string in a minimal full HTML document suitable for
 * loading in a WebView (or an iframe on web).
 */
export const wrapReportHtml = (html: string): string => `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  body { margin: 0; padding: 8px; font-family: -apple-system, BlinkMacSystemFont, sans-serif;
         font-size: 14px; color: #2c3e50; line-height: 1.5; background: transparent; }
  * { box-sizing: border-box; }
</style>
</head>
<body>${html}</body>
</html>`;

/**
 * Injected JS for a WebView showing generated HTML (wrapReportHtml output): posts
 * document.body's scroll height back via postMessage so the host can auto-size the WebView
 * within its parent ScrollView (native WebViews have no intrinsic "fit content" sizing).
 * Shared by every screen that renders generated HTML in a WebView — originally local to
 * ReportView.tsx, now used there and by AggReportView.tsx alike.
 */
export const WEBVIEW_AUTO_HEIGHT_JS = `
    (function () {
      function post() {
        window.ReactNativeWebView.postMessage(
          String(Math.max(document.body.scrollHeight, document.documentElement.scrollHeight))
        );
      }
      post();
      new MutationObserver(post).observe(document.body, { subtree: true, childList: true });
    })();
    true;
  `;
